import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Mail, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";

export default function Login() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/admin");
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid email or password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-[#0B0F17] text-white p-12 dcep-grain relative overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(50% 60% at 70% 30%, rgba(2,132,199,0.4), transparent), radial-gradient(45% 55% at 80% 75%, rgba(5,150,105,0.35), transparent)" }} />
        <a href="/" className="relative flex items-center gap-3">
          <img src={LOGO_URL} alt="DCEPtNZ" className="w-11 h-11 rounded-md" />
          <div className="font-mono text-sm font-semibold tracking-[0.18em]">DCEPtNZ</div>
        </a>
        <div className="relative">
          <h2 className="font-serif text-4xl leading-tight">The research<br />administration portal.</h2>
          <p className="text-white/60 mt-5 max-w-sm leading-relaxed">Manage articles, the interactive map, media and integrations for the Data Centre Exposure Project NZ.</p>
        </div>
        <div className="relative font-mono text-[11px] text-white/40 uppercase tracking-[0.2em]">Independent research · New Zealand</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 lg:p-12 bg-white">
        <div className="w-full max-w-sm">
          <a href="/" className="inline-flex items-center gap-2 text-sm text-[#52606D] hover:text-[#11181C] mb-8"><ArrowLeft size={15} /> Back to site</a>
          <img src={LOGO_URL} alt="DCEPtNZ" className="w-14 h-14 rounded-lg lg:hidden mb-6" />
          <h1 className="font-serif text-3xl text-[#11181C]">Administrator sign in</h1>
          <p className="text-[#52606D] mt-2 text-sm">Restricted to accounts created by a DCEPtNZ administrator.</p>

          <form onSubmit={submit} className="space-y-4 mt-8">
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52606D]" />
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address"
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-300 bg-white text-sm focus:outline-none focus:border-[#059669]" data-testid="login-email-input" />
            </div>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52606D]" />
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-300 bg-white text-sm focus:outline-none focus:border-[#059669]" data-testid="login-password-input" />
            </div>
            <button type="submit" disabled={busy} className="w-full py-3 rounded-lg bg-[#0B0F17] text-white font-medium text-sm hover:bg-[#1b2230] transition-colors disabled:opacity-60" data-testid="login-submit-button">
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
